/**
 * USER HOOK - API Methods & React Query Hooks Summary
 *
 * ===========================================
 * API METHODS (Internal Functions):
 * ===========================================
 * • verifyUser(organisationId?) - GET /user-verify?organisation_id=X - Verify current user with roles/permissions/organizations
 * • getUserById()             - GET /users/:id - Fetch detailed user information by ID
 * • getUserDetails()          - GET /users/:id?organisation_id=X - Fetch detailed user data with organization filter
 * • updateUser()              - PUT /users/:id - Update user details and roles
 * • deleteUser()              - DELETE /users/:id - Delete user by ID
 * • getOrganizationUsers()    - GET /users?organisation_id=X&role[]=...&search=... - Fetch users with optional filters
 *
 * ===========================================
 * REACT QUERY HOOKS (Exported):
 * ===========================================
 * • useUserVerification(enabled?, organisationId?)    - Query: Verify current user with organizations
 * • useUser(userId, enabled?)                         - Query: Get user by ID
 * • useUserDetails(userId, organizationId, enabled?)  - Query: Get detailed user data with organization filter
 * • useUpdateUser()                                   - Mutation: Update user details
 * • useDeleteUser()                                   - Mutation: Delete user
 * • useOrganizationUsers(organizationId, enabled?, filters?)    - Query: Get users with optional filters (roles, search, etc.)
 *
 * ===========================================
 * KEY FEATURES:
 * ===========================================
 * • Authentication via Bearer token (store + cookie fallback)
 * • Uses SSO API URL for user verification
 * • Role and permission management support
 * • User activation/deactivation capabilities
 * • 5-minute stale time with disabled auto-refetch
 * • Automatic query invalidation on mutations
 * • Comprehensive error handling (401, 403, 404, 422)
 * • Support for user profile updates (name, nationality, phone, address)
 * • Support for fetching users within a specific organization
 * ===========================================
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import fetchAPI from '@/lib/fetch-api';
import type { UserVerification } from '@/types/user';

// ==================== INTERFACES ====================

interface UserData {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  nationality_id: number;
  phone_country_id: number;
  phone_number: string;
  address: string;
  roles: string[];
  permissions: string[];
  activated_at: string;
  created_at: string;
  updated_at: string;
}

interface UpdateUserRequest {
  roles?: string[];
  first_name?: string;
  last_name?: string;
  nationality_id?: number;
  address?: string;
  phone_country_id?: number;
  phone_number?: string;
  activate?: boolean;
}

interface UserDetailsData {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  nationality_id: number;
  phone_country_id: number;
  phone_number: string;
  address: string;
  city?: string;
  zip?: string;
  country_id?: number | string;
  roles: string[];
  permissions: string[];
  activated_at: string;
  created_at: string;
  updated_at: string;
}

// ==================== API FUNCTIONS ====================

/**
 * Verifies the current user and returns basic user info with roles/permissions
 * @param {number} [organisationId] - Optional organization ID to scope the verification
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<UserVerification>} The user verification data
 */
const verifyUser = async (organisationId?: number): Promise<UserVerification> => {
  const apiUrl = process.env.NEXT_PUBLIC_SSO_API_URL;

  if (!apiUrl) {
    throw new Error('NEXT_PUBLIC_SSO_API_URL environment variable is not set');
  }

  const url = new URL(`${apiUrl}/user-verify`);
  if (organisationId) {
    url.searchParams.set('organisation_id', String(organisationId));
  }

  const response = await fetchAPI<UserVerification>(url.toString(), {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to verify user');
  }

  return response.data!;
};

/**
 * Fetches detailed user information by user ID
 * @param {number} userId - The ID of the user to fetch
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<UserData>} The user data
 */
const getUserById = async (userId: number): Promise<UserData> => {
  const apiUrl = process.env.NEXT_PUBLIC_SSO_API_URL;

  if (!apiUrl) {
    throw new Error('NEXT_PUBLIC_SSO_API_URL environment variable is not set');
  }

  const response = await fetchAPI<UserData[]>(`${apiUrl}/users/${userId}`, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch user');
  }

  if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
    throw new Error('User not found');
  }

  const userData = response.data![0];
  if (!userData) {
    throw new Error('User data not found');
  }

  return userData;
};

/**
 * Updates a user's details
 * @param {number} userId - The ID of the user to update
 * @param {UpdateUserRequest} updateData - The user data to update
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<UserData>} The updated user data
 */
const updateUser = async (userId: number, updateData: UpdateUserRequest): Promise<UserData> => {
  const response = await fetchAPI<UserData>(`/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(updateData),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to update user');
  }

  return response.data!;
};

/**
 * Fetches detailed user information by user ID and organization ID
 * @param {number} userId - The ID of the user to fetch
 * @param {number} organizationId - The organization ID to filter by
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<UserDetailsData>} The detailed user data
 */
const getUserDetails = async (userId: number, organizationId: number): Promise<UserDetailsData> => {
  const apiUrl = process.env.NEXT_PUBLIC_SSO_API_URL;

  if (!apiUrl) {
    throw new Error('NEXT_PUBLIC_SSO_API_URL environment variable is not set');
  }

  const response = await fetchAPI<UserDetailsData>(
    `${apiUrl}/users/${userId}?organisation_id=${organizationId}`,
    {
      method: 'GET',
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch user details');
  }

  if (!response.data) {
    throw new Error('User details not found');
  }

  return response.data!;
};

export interface OrganizationUsersFilters {
  roles?: string[];
  search?: string;
  countryIds?: number[];
  currentPage?: number;
  perPage?: number;
}

/**
 * Fetches all users for a given organization with optional filters
 * @param {number} organizationId - The organization ID to filter by
 * @param {OrganizationUsersFilters} filters - Optional filters for roles, search, country, pagination
 * @throws {Error} If the organization ID is invalid, no token is found, or the API request fails
 * @returns {Promise<UserData[]>} The list of users in the organization
 */
const getOrganizationUsers = async (
  organizationId: number,
  filters?: OrganizationUsersFilters
): Promise<UserData[]> => {
  if (!organizationId || organizationId <= 0) {
    throw new Error('Invalid organization ID provided');
  }

  const apiUrl = process.env.NEXT_PUBLIC_SSO_API_URL;

  if (!apiUrl) {
    throw new Error('NEXT_PUBLIC_SSO_API_URL environment variable is not set');
  }

  const url = new URL(`${apiUrl}/users`);
  url.searchParams.set('organisation_id', String(organizationId));

  // Apply optional filters
  if (filters?.roles?.length) {
    filters.roles.forEach((role) => url.searchParams.append('role[]', role));
  }
  if (filters?.search) {
    url.searchParams.set('search', filters.search);
  }
  if (filters?.countryIds?.length) {
    filters.countryIds.forEach((id) => url.searchParams.append('country_id[]', String(id)));
  }
  if (filters?.currentPage) {
    url.searchParams.set('current_page', String(filters.currentPage));
  }
  if (filters?.perPage) {
    url.searchParams.set('per_page', String(filters.perPage));
  }

  const response = await fetchAPI<UserData[]>(url.toString(), {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch organization users');
  }

  return response.data!;
};

/**
 * Deletes a user by ID
 * @param {number} userId - The ID of the user to delete
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<{ message: string }>} The deletion response
 */
const deleteUser = async (userId: number): Promise<{ message: string }> => {
  const response = await fetchAPI<{ message: string }>(`/users/${userId}`, {
    method: 'DELETE',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to delete user');
  }

  return response.data!;
};

// ==================== REACT QUERY HOOKS ====================

/**
 * Hook to verify the current user
 * @param {boolean} enabled - Whether the query should be enabled
 * @param {number} [organisationId] - Optional organization ID to scope the verification
 * @returns {UseQueryResult} Query result for user verification
 */
export function useUserVerification(
  enabled: boolean = true,
  organisationId?: number
): UseQueryResult<UserVerification, Error> {
  return useQuery({
    queryKey: ['user-verification', organisationId],
    queryFn: () => verifyUser(organisationId),
    enabled,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to fetch detailed user information by ID
 * @param {number} userId - The ID of the user to fetch
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for user data
 */
export function useUser(userId: number, enabled: boolean = true): UseQueryResult<UserData, Error> {
  return useQuery({
    queryKey: ['user', userId],
    queryFn: () => getUserById(userId),
    enabled: enabled && !!userId,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to update user details
 * @returns {UseMutationResult} Mutation result for updating user
 */
export function useUpdateUser(): UseMutationResult<
  UserData,
  Error,
  { userId: number; updateData: UpdateUserRequest }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, updateData }) => updateUser(userId, updateData),
    onSuccess: (_, { userId }) => {
      // Invalidate user-related queries
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
      queryClient.invalidateQueries({ queryKey: ['user-verification'] });
    },
  });
}

/**
 * Hook to fetch detailed user information by user ID and organization ID
 * @param {number} userId - The ID of the user to fetch
 * @param {number} organizationId - The organization ID to filter by
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for detailed user data
 */
export function useUserDetails(
  userId: number,
  organizationId: number,
  enabled: boolean = true
): UseQueryResult<UserDetailsData, Error> {
  return useQuery({
    queryKey: ['user-details', userId, organizationId],
    queryFn: () => getUserDetails(userId, organizationId),
    enabled: enabled && !!userId && !!organizationId,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to fetch users within a specific organization
 * @param {number} organizationId - The organization ID to filter by
 * @param {boolean} enabled - Whether the query should be enabled
 * @param {OrganizationUsersFilters} filters - Optional filters for roles, search, country, pagination
 * @returns {UseQueryResult} Query result for organization users
 */
export function useOrganizationUsers(
  organizationId: number,
  enabled: boolean = true,
  filters?: OrganizationUsersFilters
): UseQueryResult<UserData[], Error> {
  return useQuery({
    queryKey: ['organization-users', organizationId, filters],
    queryFn: () => getOrganizationUsers(organizationId, filters),
    enabled: enabled && !!organizationId,
    staleTime: 300000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to delete a user
 * @returns {UseMutationResult} Mutation result for deleting user
 */
export function useDeleteUser(): UseMutationResult<{ message: string }, Error, number> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteUser,
    onSuccess: (_, userId) => {
      // Remove user from cache and invalidate related queries
      queryClient.removeQueries({ queryKey: ['user', userId] });
      queryClient.invalidateQueries({ queryKey: ['user-verification'] });
    },
  });
}
